.app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  background: var(--color-bg);
  color: var(--color-text);
  position: relative;
  z-index: 10;
  gap: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 64px;
  border-bottom: 2px solid var(--color-border);
  background: var(--color-surface);
  flex-shrink: 0;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-mark {
  width: 44px;
  height: 44px;
  background: var(--color-accent);
  color: white;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 22px;
  border: 2px solid var(--color-accent);
  box-shadow: 4px 4px 0px var(--color-border-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  letter-spacing: -0.02em;
}

.logo-mark:hover {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0px var(--color-accent-hover);
}

.logo-text {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 26px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-accent);
  line-height: 1;
}

.logo {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 22px;
  color: var(--color-accent);
  line-height: 1;
}

.logo-sub {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  line-height: 1;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn {
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 16px;
  border: 2px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 3px 3px 0px var(--color-border);
  transition: all 0.15s ease;
}

.btn:hover:not(:disabled) {
  transform: translate(-1px, -1px);
  box-shadow: 4px 4px 0px var(--color-accent);
  border-color: var(--color-accent);
}

.btn:active:not(:disabled) {
  transform: translate(1px, 1px);
  box-shadow: 0px 0px 0px var(--color-border);
}

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn.link {
  color: var(--color-text-muted);
}

.btn.link:hover {
  color: var(--color-text);
}

.dropdown-container {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  box-shadow: 4px 4px 0px var(--color-border);
  min-width: 200px;
  z-index: 100;
  animation: dropdown-in 0.1s ease;
}

@keyframes dropdown-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 16px;
  border: none;
  border-bottom: 2px solid var(--color-border);
  background: none;
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 12px;
  cursor: pointer;
  transition: background 0.1s;
  letter-spacing: 0.2px;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: var(--color-surface-elevated);
}

.main {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 0;
  background: var(--color-bg);
}

.editor-pane {
  flex: 3;
  border-right: 2px solid var(--color-border);
  overflow: hidden;
  min-width: 0;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
}

.pane-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-pane .cm-editor {
  flex: 1;
  overflow: hidden;
}

.stdin-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding: 10px 16px;
  border-top: 2px solid var(--color-border);
  background: var(--color-surface-elevated);
}

.stdin-caret {
  font-family: var(--font-mono);
  font-size: 15px;
  font-weight: 700;
  color: var(--color-accent);
  line-height: 1;
}

.stdin-line {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.6;
  padding: 0;
}

.stdin-line::placeholder {
  color: var(--color-text-dim);
}

html[data-theme="light"] .editor-pane .cm-editor {
  background: #f5f5f5;
}

html[data-theme="light"] .cm-content {
  background: #f5f5f5;
}

html[data-theme="light"] .cm-line {
  background: transparent;
}

.output-pane {
  flex: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  background: var(--color-surface);
}

.pane-header {
  background: var(--color-surface-elevated);
  padding: 0 16px;
  height: 36px;
  border-bottom: 2px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
}

.pane-label {
  font-family: var(--font-mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-text-dim);
}

.clear-btn {
  font-family: var(--font-mono);
  font-size: 10px;
  padding: 4px 10px;
  border: 2px solid var(--color-border);
  background: transparent;
  color: var(--color-text-dim);
  cursor: pointer;
  transition: all 0.15s;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.clear-btn:hover {
  border-color: var(--color-text-muted);
  color: var(--color-text-muted);
}

.output-content {
  flex: 1;
  padding: 16px;
  margin: 0;
  overflow: auto;
  font-family: var(--font-mono);
  font-size: 13px;
  line-height: 1.7;
  color: var(--color-text);
  background: var(--color-surface);
  border-top: 2px solid var(--color-border);
  white-space: pre-wrap;
  word-break: break-word;
}

.output-content:empty::before {
  content: '// EXECUTE PROGRAM TO SEE OUTPUT';
  color: var(--color-text-dim);
}

.statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 24px;
  height: 48px;
  border-top: 2px solid var(--color-border);
  background: var(--color-surface);
  flex-shrink: 0;
}

.statusbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.statusbar-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.run-btn {
  display: none;
}

@media (min-width: 769px) {
  .run-btn-desktop {
    display: inline-flex;
  }
}

@media (max-width: 768px) {
  .run-btn {
    display: inline-flex;
  }
}

.run-btn {
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 20px;
  border: 2px solid var(--color-accent);
  background: var(--color-accent);
  color: white;
  cursor: pointer;
  box-shadow: 4px 4px 0px var(--color-border);
  transition: all 0.15s ease;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.run-btn:hover:not(:disabled) {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0px var(--color-accent);
  background: var(--color-accent-hover);
}

.run-btn:active:not(:disabled) {
  transform: translate(2px, 2px);
  box-shadow: 0px 0px 0px var(--color-border);
}

.run-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.status-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-dim);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: color 0.2s;
}

.status-text.has-errors {
  color: var(--color-error);
  font-weight: 600;
}

.status-text.is-running {
  color: var(--color-warning);
}

.footer-link {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--color-text-dim);
  text-decoration: none;
  transition: color 0.15s;
  letter-spacing: 0.05em;
}

.footer-link:hover {
  color: var(--color-accent);
}

.boot-overlay {
  position: fixed;
  inset: 0;
  background: var(--color-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.boot-content {
  text-align: center;
}

.boot-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.boot-dots span {
  width: 12px;
  height: 12px;
  background: var(--color-accent);
  border: 2px solid var(--color-accent);
  box-shadow: 2px 2px 0px var(--color-border);
  animation: boot-bounce 1.4s ease-in-out infinite both;
}

.boot-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.boot-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes boot-bounce {
  0%, 80%, 100% {
    transform: scale(0.5);
    opacity: 0.3;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.boot-text {
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-muted);
}

.toast {
  position: fixed;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  box-shadow: 4px 4px 0px var(--color-border);
  padding: 10px 24px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text);
  z-index: 1001;
  animation: toast-in 0.15s ease, toast-out 0.15s ease 1.35s forwards;
  pointer-events: none;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

@keyframes toast-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes toast-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

.run-btn-desktop {
  display: inline-flex;
  font-family: var(--font-display);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 20px;
  border: 2px solid var(--color-accent);
  background: var(--color-accent);
  color: white;
  cursor: pointer;
  box-shadow: 4px 4px 0px var(--color-border);
  transition: all 0.15s ease;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.run-btn-desktop:hover:not(:disabled) {
  transform: translate(-2px, -2px);
  box-shadow: 6px 6px 0px var(--color-accent);
  background: var(--color-accent-hover);
}

.run-btn-desktop:active:not(:disabled) {
  transform: translate(2px, 2px);
  box-shadow: 0px 0px 0px var(--color-border);
}

.run-btn-desktop:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .run-btn-desktop {
    display: none;
  }

  .main {
    flex-direction: column;
  }

  .editor-pane {
    flex: 1;
    border-right: none;
    border-bottom: 2px solid var(--color-border);
    min-height: 200px;
  }

  .output-pane {
    flex: 1;
    min-height: 150px;
  }

  .toolbar {
    padding: 0 12px;
    height: 56px;
  }

  .logo-mark {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  .logo-text {
    font-size: 16px;
  }

  .toolbar-right {
    gap: 4px;
  }

  .btn {
    padding: 6px 10px;
    font-size: 11px;
    box-shadow: 2px 2px 0px var(--color-border);
  }

  .btn:hover:not(:disabled) {
    box-shadow: 4px 4px 0px var(--color-accent);
  }

  .btn.link {
    display: none;
  }

  .logo-sub {
    display: none;
  }

  .statusbar {
    padding: 0 12px;
    height: auto;
    min-height: 52px;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .statusbar-left {
    gap: 10px;
    flex: 1;
  }

  .statusbar-right {
    gap: 12px;
    width: 100%;
    justify-content: flex-start;
    order: -1;
    border-bottom: 2px solid var(--color-border);
    padding-bottom: 8px;
    margin-bottom: 2px;
  }

  .run-btn {
    display: inline-flex;
    padding: 6px 14px;
    font-size: 11px;
    box-shadow: 2px 2px 0px var(--color-border);
  }

  .run-btn:hover:not(:disabled) {
    box-shadow: 4px 4px 0px var(--color-accent);
  }

  .status-text {
    font-size: 11px;
  }

  .footer-link {
    font-size: 10px;
  }

  .output-content {
    padding: 12px;
    font-size: 12px;
  }

  .terminal-header {
    padding: 8px 12px;
  }

  .dropdown-menu {
    right: -10px;
    min-width: 160px;
    box-shadow: 2px 2px 0px var(--color-border);
  }

  .dropdown-item {
    padding: 8px 12px;
    font-size: 11px;
  }
}
